import React, { useContext } from "react";
import { EmployeesContext } from "../context/EmployeesContext";
import { theme } from "../styles/Theme";
import SearchEmployees from "../components/SearchEmployees";
import {
  mergeStyleSets,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Spinner,
  Stack,
  StackItem,
} from "@fluentui/react";
import { stackTokens } from "../styles/stackConstants";
import PageLayoutStatic from "../components/PageLayoutStatic";
import { Link, useParams } from "react-router-dom";
import useOriginalImage from "../hooks/useOriginalImage";
import useEmployee from "../hooks/useEmployee";
import { ADMIN, DOWNLOADER, hasRequiredRoles } from "../auth";
import { AuthContext } from "../context/AuthContext";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { globalClasses } from "../styles/GlobalClasses";

const containerStyling = mergeStyleSets({
  root: {
    flex: "1",
    minWidth: "300px",
    maxWidth: "600px",
  },
});

const stackItemStyling = mergeStyleSets({
  root: {
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    padding: "2rem",
  },
});

export default function OriginalsPage() {
  const { identity } = useContext(AuthContext);

  const hasUserAccess = hasRequiredRoles(identity, [ADMIN, DOWNLOADER]);


  const { employeeNo } = useParams();

  const [employee, employeeIsLoading] = useEmployee(employeeNo);


  const [
    initialOriginalImageUrl,
    initialOriginalImageFile,
    initialOriginalImageIsLoading,
    initialOriginalImagePercentReceived,
  ] = useOriginalImage(employeeNo);

  const hasOriginalImage = initialOriginalImageUrl && initialOriginalImageFile && initialOriginalImageFile.size > 0;
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [downloadUrl, setDownloadUrl] = React.useState(null);

  const handleDownloadClick = (e) => {
    e.preventDefault();
    setShowConfirm(true);
    setDownloadUrl(initialOriginalImageUrl);
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    if (downloadUrl) {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${employeeNo}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setDownloadUrl(null);
    }
  };

  const handleCancel = () => {
    setShowConfirm(false);
    setDownloadUrl(null);
  };

  return (
    <PageLayoutStatic>
      <AuthenticatedTemplate>
        {identity && !hasUserAccess && (
          <MessageBar messageBarType={MessageBarType.warning}>
            Du saknar rättigheter att ladda ned originalbilder. Kontakta IT för
            behörighet
          </MessageBar>
        )}
        {hasUserAccess && (

      <Stack tokens={stackTokens} styles={containerStyling}>
        <Stack>
          <StackItem>
            <h1 style={theme.fonts.xLarge}>Sök</h1>
          </StackItem>
          <SearchEmployees targetPageUrl="original" />
        </Stack>
        {!employee && (
          <Stack>
            <StackItem>
              <MessageBar messageBarType={MessageBarType.info}>
                Här kan du ladda ned bilder på våra medarbetare. Observera följande:
                <ul>
                  <li>Alla medarbetare finns inte med i bilddatabasen</li>
                  <li>Bildkvaliteten varierar - vissa originalbilder är av lägre kvalitet</li>
                  <li>En del av bilderna är i svartvitt, en del är i färg</li>
                </ul>
                <p>
                  <b>Viktigt att veta:</b><br />
                  Personbilder räknas som personuppgifter enligt GDPR. Undvik att spara eller sprida bilder i onödan.
                </p>
                <p>
                  <b>Har du frågor?</b> Kontakta Louise Jerkland.
                </p>
              </MessageBar>
            </StackItem>
          </Stack>
        )}
        {employee && (
          <Stack>
            {employee && (
              <h2 style={theme.fonts.large}>
                {employee.name} {employee.employeeId && `(${employee.employeeId})`} 
              </h2>
            )}
            {initialOriginalImageIsLoading && (
              <StackItem styles={stackItemStyling}>
                <Spinner
                  label={`Laddar originalbild... ${initialOriginalImagePercentReceived}%`}
                />
              </StackItem>
            )}
            {!initialOriginalImageIsLoading && hasOriginalImage && (
              <>
                <StackItem>
                  <img style={{ maxWidth: "100%" }} src={initialOriginalImageUrl} alt="Original" />
                </StackItem>
                <StackItem styles={stackItemStyling}>
                  <PrimaryButton
                    text="Ladda ner originalbild"
                    iconProps={{ iconName: "Download" }}
                    onClick={handleDownloadClick}
                  />
                </StackItem>
                {showConfirm && (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    background: 'rgba(0,0,0,0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                  }}>
                    <div style={{
                      background: '#fff',
                      padding: '2rem',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      maxWidth: '400px',
                      textAlign: 'center'
                    }}>
                      <p style={{ marginBottom: '1.5rem' }}>
                        Genom att ladda ner originalbilden intygar du att du <b>inte kommer att spara bilden på din dator</b> eller annan lokal lagring efter det att du har jobbat klart med bilden.
                      </p>
                      <Stack horizontal tokens={{ childrenGap: 16 }} horizontalAlign="center">
                        <PrimaryButton text="Jag förstår och bekräftar" onClick={handleConfirm} />
                        <PrimaryButton text="Avbryt" onClick={handleCancel} style={{ background: '#eee', color: '#333' }} />
                      </Stack>
                    </div>
                  </div>
                )}
              </>

            )}

            {!initialOriginalImageIsLoading && !hasOriginalImage && (
              <StackItem styles={stackItemStyling}>
                <MessageBar>
                  Ingen originalbild tillgänglig för nedladdning.
                </MessageBar>
              </StackItem>
            )}
          </Stack>
        )}
      </Stack>


        )}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <MessageBar messageBarType={MessageBarType.warning}>
          Du är inte inloggad.
          <Link to="/login" className={globalClasses.link}>
            Logga in här
          </Link>
        </MessageBar>
      </UnauthenticatedTemplate>


    </PageLayoutStatic>
  );
}
